import { useRef, useEffect } from 'react';

/**
 * Use an interval in a way that's aware of a component's lifecycle.
 * 
 * @param {function} callback The function to call at the specified interval.
 * @param {number} delay The time in ms to wait before intervals.
 * @param {boolean} runImmediately Whether to run the callback immediately when the hook is mounted.
 * 
 * @returns {undefined}
 */
const useInterval = (callback: () => void, delay: number, runImmediately?: boolean): void => {
  const savedCallback = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      if (savedCallback.current) {
        savedCallback.current();
      }
    }

    if (runImmediately && savedCallback.current) {
      savedCallback.current();
    }

    if (delay !== null) {
      const interval = setInterval(tick, delay);
      return () => {
        clearInterval(interval);
      };
    }
  }, [delay, runImmediately]);
};

export default useInterval;